/**
 * PercentInput — input that DISPLAYS percent (e.g. "10" or "10,3") and EMITS the
 * decimal fraction (e.g. 0.1, 0.103) used internally by the backend.
 *
 * Behavior:
 *   value:    decimal fraction (0..1+) — e.g. 0.1 for 10%
 *   onChange: callback(newDecimalFraction)
 *
 * Displays the value multiplied by 100 with up to 2 decimals.
 * Accepts both "." and "," as decimal separator (Italian users tend to type "10,3").
 */
import React, { useState, useEffect } from "react";
import { Input } from "./input";

function toDisplay(v) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(v) * 100;
  if (!isFinite(num)) return "";
  // up to 4 decimals visible but trimmed
  return parseFloat(num.toFixed(4)).toString().replace(".", ",");
}

function parseDisplay(s) {
  if (s === "" || s === null || s === undefined) return null;
  const cleaned = String(s).replace(",", ".").trim();
  const n = parseFloat(cleaned);
  if (isNaN(n)) return null;
  return n / 100;
}

export const PercentInput = React.forwardRef(function PercentInput(
  { value, onChange, placeholder = "Es. 10 = 10%", disabled, className = "", ...rest },
  ref,
) {
  const [text, setText] = useState(() => toDisplay(value));

  // Sync when parent value changes (only if user is not actively typing/clearing the field).
  // We DO NOT re-sync when text === "" to avoid the "leading 0" bug after Ctrl+A+Delete.
  useEffect(() => {
    if (text === "") return;
    const display = toDisplay(value);
    const currentParsed = parseDisplay(text);
    // Only re-sync from parent if the parent's value diverged from what we'd emit
    if (currentParsed === null || Math.abs((currentParsed ?? 0) - (Number(value) || 0)) > 1e-9) {
      setText(display);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFocus = (e) => {
    // UX polish: auto-select on focus so a single keystroke replaces the value cleanly.
    try { e.target.select(); } catch (_) { /* noop */ }
  };

  const handleChange = (e) => {
    // INSTANT dot → comma conversion as user types (Italian locale)
    const raw = e.target.value.replace(/\./g, ",");
    // Allow empty, partial values like "10,", ",", "10"
    if (raw === "" || /^-?\d*,?\d*$/.test(raw)) {
      setText(raw);
      if (raw === "" || raw === "-" || raw === ",") {
        // Do NOT emit onChange(0) here — leave parent state untouched until commit.
        // Prevents "leading 0" bug after Ctrl+A+Delete.
        return;
      }
      const parsed = parseDisplay(raw);
      if (parsed !== null) onChange?.(parsed);
    }
  };

  const handleBlur = () => {
    // On blur, commit 0 if empty/invalid, then re-format display.
    const parsed = parseDisplay(text);
    if (text === "" || parsed === null) {
      onChange?.(0);
      setText(toDisplay(0));
    } else {
      setText(toDisplay(parsed));
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="pr-7"
        {...rest}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 text-sm pointer-events-none select-none">%</span>
    </div>
  );
});

export default PercentInput;
